import React from 'react';
import { formatDate, formatDateForString, formatNumber } from 'utils/formatter';
import * as commonReportComponents from '../../components/AssetChange';
import { yyyymmddToDate } from '../DailyReport';
import {
  addSignCommaFormat,
  addSignCommaFormatPercentage,
  commaFormat,
  getSign,
  Sign,
} from 'components/features/new-report/utils/formatter';
import { day } from 'components/features/new-report/weekly-report/WeeklyReport';
import Loading from 'components/commons/loading/Loading';
import { GetReportResult } from 'config/api';

type DateInformationArgs = { date: Date };

const getDateInformation = ({ date }: DateInformationArgs): React.FC => () => {
  return (
    <div className="flex flex-col items-center flex-1">
      <div className="flex items-baseline mb-1 text-sm leading-tight">
        {formatDate(date, 'yyyy年')}
      </div>
      <div className="text-base font-bold">
        {formatDate(date, 'M月d日(ddd)')}
      </div>
    </div>
  );
};

const AssetChangeInternal = commonReportComponents.AssetChange;

export const AssetChange = ({
  report: report_,
}: {
  report: GetReportResult;
}): React.ReactElement | null => {
  const report = report_.Day;

  if (
    report === null ||
    report.Base.From === null ||
    report.Base.TotalAssetAmount === null ||
    report.Base.DiffTotalAssetAmount === null ||
    report.Base.DiffTotalAssetRate === null ||
    report.Base.AssetFluctuationThresholdPer === null ||
    report.Base.AssetFluctuationThresholdPer === undefined
  )
    return <></>;

  const date = report.CreatedAt
    ? yyyymmddToDate(report.Base.From.toString())
    : null;

  if (!date)
    return (
      <>
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '120px',
            paddingBottom: '120px',
          }}
        />
      </>
    );

  const DateInformation = getDateInformation({ date });

  const assetFluctuationThresholdPer = report.Base.AssetFluctuationThresholdPer;

  const diffTotalAssetRate = report.Base.DiffTotalAssetRate;

  const val =
    diffTotalAssetRate > 0
      ? assetFluctuationThresholdPer
      : -assetFluctuationThresholdPer;

  const assetFluctuationThresholdPerDisp =
    Math.abs(assetFluctuationThresholdPer) <= Math.abs(diffTotalAssetRate * 100)
      ? val
      : null;

  const isItemMask =
    Number(formatDateForString(report.CreatedAt, 'yyyyMMdd')) === day;

  return (
    <>
      <AssetChangeInternal
        {...{
          DateInformation,
          goNext: null,
          goPrevious: null,
          periodVerbose: '日',
          updateInfoString:
            report.CreatedAt &&
            formatDate(
              new Date(report.CreatedAt.replace(' ', 'T')),
              'yyyy/M/d HH:mm'
            ),
          asset: {
            assetAmountChange: addSignCommaFormat(
              report.Base.DiffTotalAssetAmount
            ),
            assetAmountChangeSign: getSign(report.Base.DiffTotalAssetAmount),
            comparisonToLast: addSignCommaFormatPercentage(
              report.Base.DiffTotalAssetRate,
              { numberOfDecimals: 2 }
            ),
            totalAssetAmount: commaFormat(report.Base.TotalAssetAmount),
          },
          assetFluctuationThresholdPer:
            assetFluctuationThresholdPerDisp !== null
              ? formatNumber(assetFluctuationThresholdPerDisp, {
                  withPlusSign: true,
                })
              : null,
          assetFluctuationThresholdPerSign:
            assetFluctuationThresholdPerDisp !== null
              ? getSign(assetFluctuationThresholdPerDisp)
              : ('zero' as Sign),
          isItemMask,
        }}
      />
    </>
  );
};
